import * as queryString from 'query-string';
import register from './events';
import * as uuidv4 from 'uuid/v4';

export interface ILocationObject {
  [key: string]: string;
}

export interface IPageViewCache {
  currentUrl: string;
  uid: string;
}

window.addEventListener('DOMContentLoaded', refresh, false);
window.addEventListener('hashchange', refresh, false);

let routeStartTime: number;

function refresh() {
  routeStartTime = +new Date();
}

export default class StReportSdk {
  private readonly disableGetRequireQsFromUrl: any;
  private readonly qsWhiteList: string[];
  private readonly apiAddress: string;
  private readonly projectIdentifier: string;
  private readonly debug: boolean;
  private readonly eventTags: string[];

  /**
   * 构造函数
   * @param {boolean} disableGetRequireQsFromUrl - 是否禁止通过 url 拉取所需的 query string 以用于后续上报
   * @param {string[]} qsWhiteList -  query string  白名单
   * @param {string} apiAddress 上报 API 地址
   * @param {string} projectIdentifier 项目标识（[A-Za-z_]+{4,32},内部约定）
   * @param eventTags [string[]] - 事件标签数组（格式与时间分组一致），可选
   * @param {boolean} debug 是否启用调试模式
   */
  constructor({disableGetRequireQsFromUrl = false, qsWhiteList = <Array<string>>[], apiAddress, projectIdentifier, eventTags, debug = false} = {} as any) {
    this.disableGetRequireQsFromUrl = disableGetRequireQsFromUrl;
    this.qsWhiteList = qsWhiteList;
    this.apiAddress = apiAddress;
    this.eventTags = eventTags;
    this.projectIdentifier = projectIdentifier;
    this.debug = debug;
  }

  /**
   * 默认 props
   */
  public static defaultProps = {
    qsWhiteList: [
      'utm_source',
      'utm_medium',
      'utm_term',
      'utm_content',
      'utm_campaign'
    ],
    apiAddress: '//st.xoyo.com/report/web',
  };

  /**
   * 获取默认实例
   * @param {string} projectIdentifier - 项目唯一标识
   * @param eventTags [string[]] - 事件标签数组（格式与时间分组一致），可选
   * @param {boolean} debug - 是否启用调试模式
   * @return {StReportSdk} - 返回当前实例
   */
  public static getInstance({projectIdentifier, eventTags} = {} as any, debug: boolean): StReportSdk {
    if (typeof arguments[0] === 'string') {
      projectIdentifier = arguments[0];
    }
    return new StReportSdk({
      ...StReportSdk.defaultProps,
      projectIdentifier,
      eventTags,
      debug
    });
  }

  /**
   * 单例值, 用于存储 url 和 uid,
   * 请勿直接使用该变量, 请使用 getPageViewId 访问内部值
   * @type {IPageViewCache}
   */
  private static pageViewCache = {} as IPageViewCache;

  /**
   * 获取 page view id
   * @return {string}
   */
  private static getPageViewId(): string {
    const {uid} = StReportSdk.pageViewCache;
    // 暂时忽略单页面的情况
    // if (location.href !== currentUrl) {
    //   StReportSdk.pageViewCache = {
    //     currentUrl: location.href,
    //     uid: uuidv4()
    //   };
    // }
    if (!uid) {
      StReportSdk.pageViewCache = {
        currentUrl: location.href,
        uid: uuidv4()
      };
    }
    return StReportSdk.pageViewCache.uid;
  }

  /**
   * 上报
   * @param projectIdentifier [string] - 项目标识（[A-Za-z_]+{4, },内部约定），必选
   * @param eventName [string] - 事件名称，（格式与时间分组一致），可选
   * @param eventGroup [string] - 事件分组（[A-Za-z_\-]+{1,},内部约定），可选
   * @param eventDescription [string]- 事件描述（字符串）
   * @param eventDataValue [object] - 事件额外数据对象，kv对，可选, 如: evd[k1]=v1&evd[k2]
   * @param eventTags [string[]] - 事件标签数组（格式与时间分组一致），可选
   * @param referer {string} - 事件标签数组（格式与时间分组一致），可选
   */
  public report({projectIdentifier, eventName, eventGroup, eventDescription, eventDataValue, eventTags = <string[]>[], referer} = {} as any) {
    const whiteListLocationSearchObject = this.disableGetRequireQsFromUrl ? {} : this.getWhiteListLocationSearchObject();
    const apiAddress = this.apiAddress;
    const p = projectIdentifier || this.projectIdentifier;
    if (typeof eventGroup !== 'string') {
      throw new Error('事件分组 event group 必须为 string 类型');
    }
    const ev_g = eventGroup;
    const ev_desc = eventDescription;
    const ev_d = eventDataValue;
    const ev_n = eventName;
    const ev_tag = (Array.isArray(eventTags) && eventTags.length) ? eventTags : this.eventTags;
    const pv_id = StReportSdk.getPageViewId();
    const prev_ref = document.referrer;
    const customRequestParams = {p, ev_g, ev_desc, ev_d, ev_n, ev_tag, pv_id, prev_ref};
    const requestParams = StReportSdk.convertObjectToRequestParams(
      Object.assign(customRequestParams, whiteListLocationSearchObject)
    );
    const finalRequestUrl = `${apiAddress}?${encodeURIComponent(requestParams)}`;
    this.debugLog(`report custom request params: `, customRequestParams);
    this.debugLog(`report white list location search object: `, whiteListLocationSearchObject);
    this.debugLog(`report request params: `, requestParams);
    this.debugLog(`report api address: `, apiAddress);
    this.debugLog(`report final request url: `, finalRequestUrl);
    StReportSdk.reportRequest(finalRequestUrl);
  }

  /**
   * 追踪点击事件。
   * trackClick('ViewProduct');
   * @param eventName {string} - 必选。表示要追踪的事件名。
   * @param properties {object} - 可选。为了保证统一接口，properties可选填，但对于百度统计无效。
   */
  public trackClick(eventName: string, properties: object): void {
    this.report({eventName, eventDataValue: properties});
  }

  /**
   * 跟踪页面初始化时候上报 用于记录PV
   */
  public trackPageLoad(): void {
    register('load', () => this.trackClick('load_page', {td_pl: StReportSdk.getDiffRouteTime()}));
  }

  /**
   * 计算 route 时间差异
   */
  private static getDiffRouteTime() {
    if (routeStartTime) {
      return +new Date() - routeStartTime;
    } else {
      return 0;
    }
  }

  /**
   * 根据当前白名单过滤获取 Location Search Object
   * @return {ILocationObject}
   */
  private getWhiteListLocationSearchObject(): ILocationObject {
    const qsWhiteList = this.qsWhiteList;
    const locationSearchObject = StReportSdk.getLocationSearchObject();
    return Object.keys(locationSearchObject).reduce((result: ILocationObject, key: string) => {
      if (qsWhiteList.indexOf(key) > -1) {
        result[key] = locationSearchObject[key];
      }
      return result;
    }, {})
  }

  /**
   * 调试log
   * @param {string} message
   * @param {*} extraMessage
   */
  private debugLog(message: string, extraMessage: any): void {
    if (this.debug) {
      console.log(`st-report-sdk: ${message}`, extraMessage);
    }
  }

  /**
   * 获取 location.search 中 query string 对象
   * 输入: "?foo=bar"
   * 输出 {foo: 'bar'}
   * @return {ILocationObject}
   */
  private static getLocationSearchObject(): ILocationObject {
    return queryString.parse(location.search);
  }

  /**
   * 将请求对象转换
   * @param obj
   * @return {string}
   */
  private static convertObjectToRequestParams(obj: any): string {
    return JSON.stringify(obj);
  }

  /**
   * 上报请求
   * @param {string} requestUrl
   */
  private static reportRequest(requestUrl: string): void {
    new Image().src = requestUrl;
  }
}



// WEBPACK FOOTER //
// ./src/st-report-sdk.ts